import React, { useMemo, useState } from 'react';
import { TrackerCatalyst } from '../../types';
import TrackerIcon from './TrackerIcon';
import { flagged, today } from './helpers';

interface Props {
  rows: TrackerCatalyst[];
  watchedIds: Set<string>;
  onSelect: (row: TrackerCatalyst) => void;
}

function monthMatrix(year: number, month: number): Array<{ date: string; inMonth: boolean }> {
  const first = new Date(Date.UTC(year, month, 1));
  const startDow = first.getUTCDay();
  const cells: Array<{ date: string; inMonth: boolean }> = [];
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - startDow);
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    cells.push({ date: d.toISOString().slice(0, 10), inMonth: d.getUTCMonth() === month });
  }
  return cells;
}

/** The `.cal` / `.cal-grid` month calendar from tracker.js's renderCal(). */
const CatalystCalendarView: React.FC<Props> = ({ rows, watchedIds, onSelect }) => {
  const TODAY = today();
  const [cursor, setCursor] = useState(() => {
    const [y, m] = TODAY.split('-');
    return { year: +y, month: +m - 1 };
  });
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const byDate = useMemo(() => {
    const map = new Map<string, TrackerCatalyst[]>();
    for (const r of rows) {
      if (!r.date) continue;
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date)!.push(r);
    }
    return map;
  }, [rows]);

  const undated = rows.filter((r) => !r.date).length;
  const cells = useMemo(() => monthMatrix(cursor.year, cursor.month), [cursor]);
  const label = new Date(Date.UTC(cursor.year, cursor.month, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const shift = (delta: number) => {
    const d = new Date(Date.UTC(cursor.year, cursor.month + delta, 1));
    setCursor({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
  };

  return (
    <div className="cal">
      <div className="cal-head">
        <button type="button" className="ib" aria-label="Previous month" onClick={() => shift(-1)}><TrackerIcon name="left" /></button>
        <h2>{label}</h2>
        <button type="button" className="ib" aria-label="Next month" onClick={() => shift(1)}><TrackerIcon name="right" /></button>
        <button type="button" className="ib" onClick={() => { const [y, m] = TODAY.split('-'); setCursor({ year: +y, month: +m - 1 }); }}>Today</button>
      </div>
      <div className="cal-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div className="dow" key={d}>{d}</div>)}
        {cells.map((cell) => {
          const dayRows = byDate.get(cell.date) || [];
          const isToday = cell.date === TODAY;
          const expanded = expandedDay === cell.date;
          const shown = expanded ? dayRows : dayRows.slice(0, 3);
          return (
            <div key={cell.date} className={`day${cell.inMonth ? '' : ' out'}${isToday ? ' today' : ''}`}>
              <span className="n">{+cell.date.slice(8)}</span>
              {shown.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`cchip ${r.status === 'past' ? (r.outcome || 'pending') : ''}${flagged(r) ? ' dl' : ''}`}
                  title={`${r.asset || r.company} — ${r.event || ''}`}
                  onClick={() => onSelect(r)}
                >
                  {watchedIds.has(r.id) ? '★ ' : ''}{r.ticker || (r.company || '').split(' ')[0]} · {r.asset}
                </button>
              ))}
              {!expanded && dayRows.length > 3 && (
                <button type="button" className="more" onClick={() => setExpandedDay(cell.date)}>+{dayRows.length - 3} more</button>
              )}
            </div>
          );
        })}
      </div>
      {undated > 0 && <p className="undated">{undated} catalyst{undated === 1 ? '' : 's'} with no guided date yet (not shown on the calendar).</p>}
    </div>
  );
};

export default CatalystCalendarView;
